import {
  LocalOfferLocation,
  OfferSource,
  PerkSectionsData,
  PerkSectionsDataItemsResultTypenames,
  VendorItem,
} from '@varo/graphql-schema-types';
import { router } from 'expo-router';
import { mapVendorItemToLocalOfferLocation } from 'features/rewards/helpers/mapVendorItemToLocalOfferLocation';
import nearbyLoadingNode from 'features/rewards/RewardsHome/adapters/nearbyLoadingNode';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { VXLComponentId, VXLGroupId, VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

type NearbySectionRendererParams = {
  section: PerkSectionsData;
  centerLocation?: google.maps.LatLngLiteral;
  nearbyLoading?: boolean;
  handleMapPressAnalytics: () => void;
};

const renderNearbySection = ({
  section,
  centerLocation,
  nearbyLoading,
  handleMapPressAnalytics,
}: NearbySectionRendererParams): VXLNode[] => {
  if (nearbyLoading) {
    return [nearbyLoadingNode(`nearby_loading_${section.id}`)];
  }

  const vendors = (() => {
    try {
      if (section && section.itemsV2.length > 0) {
        return section.itemsV2
          .filter(
            (item): item is VendorItem =>
              item.__typename ===
              PerkSectionsDataItemsResultTypenames.VendorItem,
          )
          .map(mapVendorItemToLocalOfferLocation)
          .filter((x): x is LocalOfferLocation => Boolean(x));
      }
      return [];
    } catch {}
    return [];
  })();

  if (!centerLocation) {
    return [
      {
        id: section.id,
        name: VXLComponentId.MAPS_CARD,
        props: {
          onPress: () => {
            handleMapPressAnalytics();
            router.push('/rewards/nearby-offers/map');
          },
        },
      },
    ];
  }

  if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID) {
    return [
      {
        id: section.id,
        name: VXLComponentId.MAPS_CARD,
        props: {
          onPress: () => router.push('/rewards/nearby-offers/map'),
        },
      },
    ];
  }
  return [
    {
      id: `nearby_map_carousel_group_${section.id}`,
      name: VXLGroupId.COMPONENT_GROUP,
      props: {
        components: [
          {
            id: `nearby_map_${section.id}`,
            name: VXLComponentId.DYNAMIC_MAP_CARD,
            props: {
              id: `nearby-${section.id}`,
              markerData: vendors,
              cameraLocation: centerLocation,
              onMarkerPress: () => {},
              onMapCardPress: () => {
                handleMapPressAnalytics();
                router.push('/rewards/nearby-offers/map');
              },
              allowMapInteractions: false,
              containerStyle: {
                padding: theme.spacing.m,
              },
            },
          },
          {
            id: `nearby_carousel_${section.id}`,
            name: VXLComponentId.NEARBY_OFFERS_CAROUSEL,
            props: {
              nearbyOffers: vendors.filter(
                (vendor) => vendor.offerSource === OfferSource.Upside,
              ),
              coordinates: centerLocation,
            },
          },
        ],
      },
    },
  ];
};

type UseNearbySectionRendererParams = {
  coordinates?: google.maps.LatLngLiteral;
  isLoading?: boolean;
};

export const useNearbySectionRenderer = ({
  coordinates,
  isLoading,
}: UseNearbySectionRendererParams) => {
  const handleMapPressAnalytics = usePressableAnalytics({
    analyticsId: 'nearbyMapSelected',
  });

  return {
    nearbyRenderer: (section: PerkSectionsData): VXLNode[] =>
      renderNearbySection({
        section,
        centerLocation: coordinates,
        nearbyLoading: isLoading,
        handleMapPressAnalytics,
      }),
  };
};
